















































import { mapGetters, mapMutations, mapState } from 'vuex'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { USER_NS } from '~/store/modules/shared/user/state'
import Breadcrumbs from '~/components/car/Breadcrumbs.vue'
import Header from '~/components/car/classifieds/form/header/Header.vue'
import Fieldsets from '~/components/car/classifieds/form/fieldsets/Fieldsets.vue'
import NewClassifiedRegisterPrompt from '~/components/car/classifieds/form/header/info/NewClassifiedRegisterPrompt.vue'
import {
  RESET_STATE,
  SET_CATEGORY_ID,
  SET_CONFIG,
  SET_EDIT_MODE,
  SET_PAGE_KEY,
  SET_ROOT_CATEGORY_ID,
  SET_USER_TELEPHONES
} from '~/store/modules/shared/classifieds/form/mutation-types'
import { CategoryId } from '~/models/category/types'
import { MetaItem } from '~/models/head/types'
import { getRouteMetaItem } from '~/utils/router'
import TopAlert from '~/components/car/classifieds/form/TopAlert.vue'
import HeadService from '~/services/HeadService'
import { getFormConfig } from '~/utils/form'
import CAnonGuardNewClassifiedModal from '~/components/shared/configurable/user/CAnonGuardNewClassifiedModal.vue'
import { ANON_GUARD_COOKIE } from '~/constants/anon-guard'
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  useRoute,
  useStore
} from '@nuxtjs/composition-api'
import useClassifiedFormSubmit from '~/compositions/form/submit'
import CConfirmationModal from '~/components/shared/configurable/modal/CConfirmationModal.vue'
import RouteGuardService from '~/services/RouteGuardService'
import { useNamespacedStore } from '~/compositions/store'
import { defineComponentTranslations } from '~/utils/i18n'
import { useI18n } from '~/compositions/i18n'
import { defineVue3AsyncComponent } from '~/utils/nuxt3-migration'

export default defineComponent({
  validate({ $dep, route }) {
    if (route.name === '__classifieds_wanted_parts_new_form') {
      const routeGuardService = $dep(RouteGuardService)
      return routeGuardService.userHasAccessToWantedParts()
    } else {
      return true
    }
  },
  middleware: [
    'non-expired-manager',
    ({ route, redirect, $logger }) => {
      const categoryId = route.params.categoryId

      if (isNaN(parseInt(categoryId))) {
        $logger.captureError(
          new Error(
            `Route param categoryId is invalid! Path: ${route.fullPath}`
          )
        )

        return redirect('/classifieds/new/')
      }
    }
  ],
  components: {
    CAnonGuardNewClassifiedModal,
    Breadcrumbs,
    Header,
    Fieldsets,
    NewClassifiedRegisterPrompt,
    TopAlert,
    CConfirmationModal,
    PhoneVerificationModal: defineVue3AsyncComponent(() =>
      import('~/components/shared/account/PhoneVerificationModal.vue')
    )
  },
  async asyncData({ error: errorFn, store, route, $logger }) {
    const rootCategoryId =
      getRouteMetaItem(route, 'rootCategoryId') || CategoryId.CLASSIFIEDS
    const vehicleCategoryId = getRouteMetaItem(route, 'vehicleCategoryId')

    store.commit(`${CLASSIFIED_FORM_NS}/${RESET_STATE}`)

    store.commit(
      `${CLASSIFIED_FORM_NS}/${SET_CONFIG}`,
      getFormConfig(rootCategoryId, vehicleCategoryId)
    )

    store.commit(
      `${CLASSIFIED_FORM_NS}/${SET_CATEGORY_ID}`,
      parseInt(route.params.categoryId as string)
    )
    store.commit(`${CLASSIFIED_FORM_NS}/${SET_EDIT_MODE}`, false)
    store.commit(
      `${CLASSIFIED_FORM_NS}/${SET_ROOT_CATEGORY_ID}`,
      rootCategoryId
    )
    const { copy: classifiedIdToCopy } = route.query
    if (classifiedIdToCopy) {
      await Promise.all([
        store.dispatch(
          `${CLASSIFIED_FORM_NS}/loadCopyFieldValues`,
          classifiedIdToCopy
        ),
        store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`)
      ])

      return
    }

    try {
      if (route.query.incomplete) {
        // In case of incomplete classified, we have to load the field values before the schema
        await store.dispatch(`${CLASSIFIED_FORM_NS}/loadFieldValues`)
        await store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`)
      } else {
        await Promise.all([
          store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`),
          store.dispatch(`${CLASSIFIED_FORM_NS}/loadFieldValues`)
        ])
      }

      // the following line will force form components to re-render (for example going from edit to new)
      store.commit(`${CLASSIFIED_FORM_NS}/${SET_PAGE_KEY}`, 'new')
    } catch (err) {
      if (err.response) {
        const { data } = err.response
        const { status: statusCode, error: message } = data

        return errorFn({ statusCode, message })
      } else {
        $logger.captureError(err)

        return errorFn(500)
      }
    }
  },
  setup() {
    const {
      commit: classifiedFormCommit,
      state: formState
    } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
    const {
      onWarningConfirmationModalConfirm,
      onWarningConfirmationModalCancel,
      submitForm
    } = useClassifiedFormSubmit()

    const { t } = useI18n()
    const store = useStore()
    const route = useRoute()

    const title = computed(() => formState.title || t('new_classified'))

    function onTelephoneVerifySuccess() {
      submitForm()
      classifiedFormCommit(SET_USER_TELEPHONES, [])
    }

    const unsubscribe = store.subscribeAction({
      after: async action => {
        if (!process.client) {
          return
        }
        if (action.type === `${USER_NS}/loadUser`) {
          if (route.value.query.incomplete) {
            // In case of incomplete classified, we have to load the field values before the schema
            await store.dispatch(`${CLASSIFIED_FORM_NS}/loadFieldValues`)
            await store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`)
          } else {
            await Promise.all([
              store.dispatch(`${CLASSIFIED_FORM_NS}/loadSchema`),
              store.dispatch(`${CLASSIFIED_FORM_NS}/loadFieldValues`)
            ])
          }
        }
      }
    })

    onBeforeUnmount(() => {
      unsubscribe()
    })

    return {
      onTelephoneVerifySuccess,
      onWarningConfirmationModalConfirm,
      onWarningConfirmationModalCancel,
      title
    }
  },
  data() {
    return {
      anonGuardModalVisible: false,
      firstUserTelephone: false
    }
  },
  computed: {
    ...mapState(CLASSIFIED_FORM_NS, {
      breadcrumbs: state => state.page.breadcrumbs,
      telephones: state => state.telephones,
      warningConfirmationMessage: state => state.warningConfirmationMessage,
      isWarningConfirmationVisible: state => state.isWarningConfirmationVisible
    }),
    ...mapGetters(USER_NS, {
      userIsAnon: 'isAnon',
      userIsSingle: 'isSingle'
    })
  },

  watch: {
    telephones() {
      this.firstUserTelephone =
        this.telephones?.length > 0 ? this.telephones[0].telephone : null
    }
  },
  mounted() {
    if (this.userIsAnon && !this.$cookies.get(ANON_GUARD_COOKIE)) {
      this.anonGuardModalVisible = true
    }
  },
  methods: {
    ...mapMutations(CLASSIFIED_FORM_NS, {
      setUserTelephones: SET_USER_TELEPHONES
    })
  },
  head() {
    const { page } = this.$store.state.classifieds.form
    const {
      title,
      metadata: meta
    }: { title: string; metadata: MetaItem[] } = page
    const headService = this.$dep(HeadService)
    return headService.createHead({ title, meta })
  },
  i18n: defineComponentTranslations({
    new_classified: {
      en: 'New classified',
      el: 'Καταχώριση'
    }
  })
})
